import type { ModelMessage } from 'ai'
import { useCallback, useState } from 'react'
import { MAX_CHATS } from '@/lib/constants/chat-constants'
import type { Chat, TurnMetadata } from '@/lib/types'

export function useChatManagement() {
  const [chats, setChats] = useState<Chat[]>([])
  const [activeChatId, setActiveChatId] = useState<string | null>(null)

  const handleNewChat = useCallback(() => {
    const newChat: Chat = {
      id: crypto.randomUUID(),
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      isNew: true, // Mark as local-only until first message is saved
    }
    setChats((prev) => {
      const updated = [newChat, ...prev]
      if (updated.length > MAX_CHATS) {
        return updated.slice(0, MAX_CHATS)
      }
      return updated
    })
    setActiveChatId(newChat.id)
  }, [])

  const handleDismiss = useCallback(
    (chatId: string) => {
      setChats((prev) => {
        const filtered = prev.filter((c) => c.id !== chatId)
        // If dismissing the active chat, switch to another chat
        if (activeChatId === chatId) {
          if (filtered.length > 0) {
            setActiveChatId(filtered[0].id)
          } else {
            console.log('No other chats, creating new one')
            handleNewChat()
          }
        }
        return filtered
      })
    },
    [activeChatId, handleNewChat]
  )

  const updateMessages = useCallback(
    (
      updater: (messages: ModelMessage[]) => ModelMessage[],
      chatId?: string
    ) => {
      setChats((prev) => {
        const targetId = chatId ?? activeChatId
        if (!targetId) {
          console.warn('[updateMessages] No target chat ID')
          return prev
        }

        const idx = prev.findIndex((c) => c.id === targetId)
        if (idx === -1) {
          console.warn(
            '[updateMessages] Chat not found:',
            targetId,
            'available:',
            prev.map((c) => c.id)
          )
          return prev
        }

        const next = [...prev]
        next[idx] = {
          ...next[idx],
          messages: updater(next[idx].messages),
        }
        return next
      })
    },
    [activeChatId]
  )

  const handleSelectChatFromHistory = useCallback(
    async (id: string) => {
      // Check if chat is already in the tabs
      const existingChat = chats.find((c) => c.id === id)
      if (existingChat) {
        setActiveChatId(id)
        return
      }

      // Chat not in tabs, add it to tabs (messages will be loaded by ChatTabContent)
      const newChat: Chat = {
        id,
        title: 'Loading chat...',
        messages: [],
        createdAt: Date.now(),
      }
      setChats((prev) => {
        const updated = [newChat, ...prev]
        // Respect MAX_CHATS limit
        if (updated.length > MAX_CHATS) {
          return updated.slice(0, MAX_CHATS)
        }
        return updated
      })
      setActiveChatId(id)
    },
    [chats]
  )

  /**
   * Safely merge messages from backend - only updates if backend has MORE messages
   * than local state. This prevents wiping optimistic updates during race conditions.
   */
  const handleMessagesLoaded = useCallback(
    (chatId: string, loadedMessages: ModelMessage[], title?: string) => {
      setChats((prev) =>
        prev.map((c) => {
          if (c.id !== chatId) return c

          // CRITICAL: Only replace messages if backend has at least as many
          // This prevents wiping optimistic updates when backend is behind
          const shouldUpdateMessages = loadedMessages.length >= c.messages.length

          return {
            ...c,
            messages: shouldUpdateMessages ? loadedMessages : c.messages,
            title: title || c.title,
          }
        })
      )
    },
    []
  )

  /**
   * Update turn metadata for a chat (for snapshot actions, not for rendering)
   */
  const updateTurnMetadata = useCallback(
    (chatId: string, turnMetadata: TurnMetadata[]) => {
      setChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, turnMetadata } : c))
      )
    },
    []
  )

  /**
   * Force replace messages - use only when intentionally loading from history
   * where we know backend is authoritative (e.g., selecting from history panel)
   */
  const forceSetMessages = useCallback(
    (chatId: string, messages: ModelMessage[], title?: string) => {
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId
            ? { ...c, messages, title: title || c.title }
            : c
        )
      )
    },
    []
  )

  const createNewChatIfNeeded = useCallback(
    (targetChatId: string | null, title: string): string => {
      if (targetChatId) return targetChatId

      const newChat: Chat = {
        id: crypto.randomUUID(),
        title,
        messages: [],
        createdAt: Date.now(),
        isNew: true, // Mark as local-only until first message is saved
      }
      setChats((prev) => [newChat, ...prev])
      setActiveChatId(newChat.id)
      return newChat.id
    },
    []
  )

  const updateChatTitle = useCallback((chatId: string, title: string) => {
    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === chatId)
      if (idx === -1) return prev

      const next = [...prev]
      next[idx] = {
        ...next[idx],
        title,
      }
      return next
    })
  }, [])

  const markChatPersisted = useCallback((chatId: string) => {
    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === chatId)
      if (idx === -1) return prev

      const next = [...prev]
      next[idx] = {
        ...next[idx],
        isNew: false,
      }
      return next
    })
  }, [])

  return {
    chats,
    setChats,
    activeChatId,
    setActiveChatId,
    handleNewChat,
    handleDismiss,
    updateMessages,
    handleSelectChatFromHistory,
    handleMessagesLoaded,
    createNewChatIfNeeded,
    updateChatTitle,
    markChatPersisted,
    updateTurnMetadata,
    forceSetMessages,
  }
}
